import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../services/api';
import { Home, Plus, Edit2, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

const HouseDataPage = () => {
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredHouses, setFilteredHouses] = useState([]);
  const [selectedPanchayatFilter, setSelectedPanchayatFilter] = useState('');
  const [selectedSchemeFilter, setSelectedSchemeFilter] = useState('');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Dropdown states
  const [blockOptions, setBlockOptions] = useState([]);
  const [panchayatOptions, setPanchayatOptions] = useState([]);
  const [blockSearch, setBlockSearch] = useState('');
  const [panchayatSearch, setPanchayatSearch] = useState('');
  const [showBlockDropdown, setShowBlockDropdown] = useState(false);
  const [showPanchayatDropdown, setShowPanchayatDropdown] = useState(false);

  const blockDropdownRef = useRef(null);
  const panchayatDropdownRef = useRef(null);

  const [formData, setFormData] = useState({
    id: null,
    h_id: '',
    beneficiary: '',
    order_ref: '',
    s_year: '',
    district: '',
    block: '',
    panchayat: '',
    scheme: '',
    amount: '',
    stage: ''
  });

  const [originalData, setOriginalData] = useState(null);

  const sanctionYearOptions = ['2023-24', '2024-25', '2025-26'];
  
  const stageOptions = [
    'Basement Construction',
    'Lintel Level', 
    'Lintel Laid',
    'Roofing Level',
    'Roof Laid',
    'Plastering',
    'Physically Completed'
  ];

  useEffect(() => {
    fetchHouses();
    fetchBlocks();
  }, []);

  // Update filtered houses when houses, search term, or filters change
  useEffect(() => {
    filterHouses(searchTerm, selectedPanchayatFilter, selectedSchemeFilter);
    setCurrentPage(1); // Reset to first page when filters change
  }, [houses, searchTerm, selectedPanchayatFilter, selectedSchemeFilter]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (blockDropdownRef.current && !blockDropdownRef.current.contains(event.target)) {
        setShowBlockDropdown(false);
      }
      if (panchayatDropdownRef.current && !panchayatDropdownRef.current.contains(event.target)) {
        setShowPanchayatDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchBlocks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/project/blocks`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (response.ok && data.data) {
        setBlockOptions(data.data);
      } else {
        setBlockOptions([]);
      }
    } catch (err) {
      console.error('Error fetching blocks:', err);
      setBlockOptions([]);
    }
  };

  const fetchPanchayats = async (blockName) => {
    if (!blockName) {
      setPanchayatOptions([]);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/project/${blockName}/panchayats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (response.ok && data.data) {
        setPanchayatOptions(data.data);
      } else {
        setPanchayatOptions([]);
      }
    } catch (err) {
      console.error('Error fetching panchayats:', err);
      setPanchayatOptions([]);
    }
  };

  const filteredBlocks = blockOptions.filter(block =>
    (block.name || block).toLowerCase().includes(blockSearch.toLowerCase())
  );

  const filteredPanchayats = panchayatOptions.filter(panchayat =>
    (panchayat.name || panchayat).toLowerCase().includes(panchayatSearch.toLowerCase())
  );

  const handleBlockSelect = (block) => {
    const blockName = block.name || block;
    setFormData(prev => ({
      ...prev,
      block: blockName,
      panchayat: ''
    }));
    setBlockSearch(blockName);
    setShowBlockDropdown(false);
    setPanchayatSearch('');
    fetchPanchayats(blockName);
  };

  const handlePanchayatSelect = (panchayat) => {
    const panchayatName = panchayat.name || panchayat;
    setFormData(prev => ({
      ...prev,
      panchayat: panchayatName
    }));
    setPanchayatSearch(panchayatName);
    setShowPanchayatDropdown(false);
  };

  // Filter houses based on search term, panchayat filter, and scheme filter
  const filterHouses = (searchValue, panchayatFilter, schemeFilter) => {
    let filtered = houses;
    
    // Apply panchayat filter
    if (panchayatFilter) {
      filtered = filtered.filter(house => house.panchayat === panchayatFilter);
    }
    
    // Apply scheme filter
    if (schemeFilter) {
      filtered = filtered.filter(house => house.scheme === schemeFilter);
    }
    
    // Apply search filter
    if (searchValue.trim()) {
      filtered = filtered.filter(house => 
        house.h_id?.toLowerCase().includes(searchValue.toLowerCase()) ||
        house.beneficiary?.toLowerCase().includes(searchValue.toLowerCase()) ||
        house.block?.toLowerCase().includes(searchValue.toLowerCase()) ||
        house.panchayat?.toLowerCase().includes(searchValue.toLowerCase()) ||
        house.scheme?.toLowerCase().includes(searchValue.toLowerCase())
      );
    }
    
    setFilteredHouses(filtered);
  };

  // Get unique panchayats from houses for filter dropdown
  const getUniquePanchayats = () => {
    const panchayats = [...new Set(houses.map(house => house.panchayat).filter(Boolean))];
    return panchayats.sort();
  };

  // Get unique schemes from houses for filter dropdown
  const getUniqueSchemes = () => {
    const schemes = [...new Set(houses.map(house => house.scheme).filter(Boolean))];
    return schemes.sort();
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredHouses.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentHouses = filteredHouses.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const fetchHouses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/project/house/list`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (response.ok) {
        setHouses(data.data || []);
      } else {
        setError(data.message || 'Failed to fetch houses');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      id: null,
      h_id: '',
      beneficiary: '',
      order_ref: '',
      s_year: '',
      district: '',
      block: '',
      panchayat: '',
      scheme: '',
      amount: '',
      stage: ''
    });
    setOriginalData(null);
    setBlockSearch('');
    setPanchayatSearch('');
    setError('');
    setSuccess('');
    setEditMode(false);
    setEditIndex(null);
  };

  const handleAddNew = () => {
    resetForm();
    setShowForm(true);
    
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleEdit = (house, index) => {
    const editData = {
      id: house.id,
      h_id: house.h_id,
      beneficiary: house.beneficiary,
      order_ref: house.order_ref,
      s_year: house.s_year,
      district: house.district,
      block: house.block,
      panchayat: house.panchayat,
      scheme: house.scheme,
      amount: house.amount,
      stage: house.stage || ''
    };
    
    setFormData(editData);
    setOriginalData({ ...editData });
    
    setBlockSearch(house.block || '');
    setPanchayatSearch(house.panchayat || '');
    
    if (house.block) {
      fetchPanchayats(house.block);
    }
    
    setEditMode(true);
    setEditIndex(index);
    setShowForm(true);
    setError('');
    setSuccess('');
    
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleSubmit = async () => {
    if (!formData.h_id || !formData.beneficiary || !formData.order_ref || 
        !formData.s_year || !formData.district || !formData.block || !formData.panchayat || 
        !formData.scheme || !formData.amount || !formData.stage) {
      setError('Please fill in all required fields');
      return;
    }

    setCreating(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      let payload;

      if (editMode) {
        payload = {};
        const fieldsToCheck = ['h_id', 'beneficiary', 'order_ref', 's_year', 'district', 'block', 'panchayat', 'scheme', 'amount', 'stage'];
        
        fieldsToCheck.forEach(field => {
          if (formData[field] !== originalData[field]) {
            payload[field] = formData[field];
          }
        });

        if (Object.keys(payload).length === 0) {
          setError('No changes detected to update');
          setCreating(false);
          return;
        }
      } else {
        payload = {
          h_id: formData.h_id,
          beneficiary: formData.beneficiary,
          order_ref: formData.order_ref,
          s_year: formData.s_year,
          district: formData.district,
          block: formData.block,
          panchayat: formData.panchayat,
          scheme: formData.scheme,
          amount: formData.amount,
          stage: formData.stage
        };
      }

      const url = editMode 
        ? `${API_BASE_URL}/project/house/${formData.id}`
        : `${API_BASE_URL}/project/house`;
      
      const method = editMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (response.ok) {
        if (editMode) {
          setSuccess('House data updated successfully!');
          const updatedHouses = [...houses];
          updatedHouses[editIndex] = { ...formData };
          setHouses(updatedHouses);
        } else {
          setSuccess('House data created successfully!');
        }
        resetForm();
        setShowForm(false);
        fetchHouses();
      } else {
        setError(data.message || `Failed to ${editMode ? 'update' : 'create'} house data`);
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    resetForm();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading house data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center">
              <Home className="w-6 h-6 text-blue-600 mr-3" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">House Data Management</h1>
              </div>
            </div>
            <div className="mt-4 sm:mt-0">
              <div className="flex space-x-3">
                <button
                  onClick={handleAddNew}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition duration-200 font-medium flex items-center"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add New House
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Error Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        {/* Success Messages */}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg mb-4">
            {success}
          </div>
        )}

        {/* Add/Edit House Form */}
        {showForm && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {editMode ? 'Edit House Data' : 'Add New House Data'}
            </h2>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">House ID *</label>
                  <input
                    type="text"
                    name="h_id"
                    value={formData.h_id}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., VIL-0001"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Beneficiary Name *</label>
                  <input
                    type="text"
                    name="beneficiary"
                    value={formData.beneficiary}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter beneficiary name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Order Reference *</label>
                  <input
                    type="text"
                    name="order_ref"
                    value={formData.order_ref}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Sanction order number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sanction Year *</label>
                  <select
                    name="s_year"
                    value={formData.s_year}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Year</option>
                    {sanctionYearOptions.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">District *</label>
                  <input
                    type="text"
                    name="district"
                    value={formData.district}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="District name"
                  />
                </div>

                {/* Block Dropdown */}
                <div ref={blockDropdownRef}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Block *</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={blockSearch}
                      onChange={(e) => {
                        setBlockSearch(e.target.value);
                        setShowBlockDropdown(true);
                      }}
                      onFocus={() => setShowBlockDropdown(true)}
                      placeholder="Search Block..."
                      className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-gray-400 pointer-events-none" />
                    
                    {showBlockDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-32 overflow-auto">
                        {filteredBlocks.length > 0 ? (
                          filteredBlocks.map((block, index) => (
                            <div
                              key={block.id || index}
                              onClick={() => handleBlockSelect(block)}
                              className="px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                            >
                              {block.name || block}
                            </div>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-sm text-gray-500">No blocks found</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Panchayat Dropdown */}
                <div ref={panchayatDropdownRef}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Panchayat *</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={panchayatSearch}
                      onChange={(e) => {
                        setPanchayatSearch(e.target.value);
                        setShowPanchayatDropdown(true);
                      }}
                      onFocus={() => setShowPanchayatDropdown(true)}
                      placeholder={formData.block ? "Search Panchayat..." : "Select Block first"}
                      disabled={!formData.block}
                      className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                    <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-gray-400 pointer-events-none" />
                    
                    {showPanchayatDropdown && formData.block && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-32 overflow-auto">
                        {filteredPanchayats.length > 0 ? (
                          filteredPanchayats.map((panchayat, index) => (
                            <div
                              key={panchayat.id || index}
                              onClick={() => handlePanchayatSelect(panchayat)}
                              className="px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                            >
                              {panchayat.name || panchayat}
                            </div>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-sm text-gray-500">No panchayats found</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Scheme *</label>
                  <input
                    type="text"
                    name="scheme"
                    value={formData.scheme}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Housing scheme name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Amount *</label>
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Sanctioned amount"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Stage of Construction *</label>
                  <select
                    name="stage"
                    value={formData.stage}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Stage</option>
                    {stageOptions.map(stage => (
                      <option key={stage} value={stage}>{stage}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition duration-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={creating}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creating ? (editMode ? 'Updating...' : 'Adding...') : (editMode ? 'Update House Data' : 'Add House Data')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Houses List */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col gap-4">
              <h2 className="text-lg font-semibold text-gray-900">House Data List ({filteredHouses.length})</h2>
              
              <div className="flex flex-col lg:flex-row gap-3">
                {/* Panchayat Filter Dropdown */}
                <div className="relative w-full lg:w-48">
                  <select
                    value={selectedPanchayatFilter}
                    onChange={(e) => setSelectedPanchayatFilter(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="">All Panchayats</option>
                    {getUniquePanchayats().map((panchayat) => (
                      <option key={panchayat} value={panchayat}>
                        {panchayat}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Scheme Filter Dropdown */}
                <div className="relative w-full lg:w-48">
                  <select
                    value={selectedSchemeFilter}
                    onChange={(e) => setSelectedSchemeFilter(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="">All Schemes</option>
                    {getUniqueSchemes().map((scheme) => (
                      <option key={scheme} value={scheme}>
                        {scheme}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Search Input */}
                <div className="relative w-full lg:flex-1">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by House ID, Beneficiary, Block..."
                    className="w-full px-3 py-2 pl-9 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">House ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Beneficiary</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order Ref</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Year</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Scheme</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stage</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentHouses.map((house, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {house.h_id}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {house.beneficiary}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {house.order_ref}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {house.s_year}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      <div className="space-y-1">
                        <div>{house.district}</div>
                        <div className="text-xs text-gray-500">{house.block} → {house.panchayat}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {house.scheme}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      ₹{Number(house.amount).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                        {house.stage || 'Not Set'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      <button
                        onClick={() => handleEdit(house, startIndex + index)}
                        className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200"
                        title="Edit house data"
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {filteredHouses.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                {searchTerm || selectedPanchayatFilter || selectedSchemeFilter ? 'No houses match your search criteria.' : 'No house data found. Add your first house to get started.'}
                {(searchTerm || selectedPanchayatFilter || selectedSchemeFilter) && (
                  <p className="text-sm mt-2">Try different keywords or clear the filters.</p>
                )}
              </div>
            )}
          </div>

          {/* Pagination */}
          {filteredHouses.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">Show</span>
                  <select
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                    className="px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="text-sm text-gray-700">entries</span>
                </div>
                <div className="text-sm text-gray-700">
                  Showing {startIndex + 1} to {Math.min(endIndex, filteredHouses.length)} of {filteredHouses.length} results
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                <div className="flex gap-1">
                  {[...Array(totalPages)].map((_, index) => {
                    const page = index + 1;
                    // Show first page, last page, current page, and pages around current page
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-3 py-1 rounded-md text-sm font-medium transition duration-200 ${
                            currentPage === page
                              ? 'bg-blue-600 text-white'
                              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    } else if (
                      page === currentPage - 2 ||
                      page === currentPage + 2
                    ) {
                      return (
                        <span key={page} className="px-2 py-1 text-gray-500">
                          ...
                        </span>
                      );
                    }
                    return null;
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HouseDataPage;